import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Dropzone, InlineErrorMessage } from '@atoms/index';
import {
  MAX_FILE_SIZE,
  detectDuplicateFilesAndRename,
  disableDropzone,
  validateFile,
} from '@utils';
import { X } from '@phosphor-icons/react';
import { v4 as uuidv4 } from 'uuid';
import { ErrorMessages } from '@types';
import { usePdfConfig } from '@shared/utils';
import { ACCEPTED_FILE_TYPES } from '@constants';

export interface UploadPhotoProps {
  id?: string;
  initialFiles?: File[];
  handleFileChange: (files: File[]) => void | Promise<void>;
  handleUploadError: (error: string) => void;
  handleDelete?: (files: File[]) => void | Promise<void>;
  maxNumberOfUpload?: number;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  errorPath?: string;
  errorMessages?: ErrorMessages[];
  dropzoneLabel?: string;
}

interface ImageListProps {
  fileList: File[];
  setUploadedFiles: (files: File[]) => void;
  handleDelete?: (files: File[]) => void | Promise<void>;
}

export const ImageList: React.FC<ImageListProps> = ({
  fileList,
  setUploadedFiles,
  handleDelete,
}) => {
  if (!fileList || fileList?.length === 0) {
    return null;
  }

  const handleDeleteFile = async (selectedFile: File) => {
    const newFiles = fileList.filter((file) => file.name !== selectedFile.name);
    if (handleDelete) {
      await handleDelete(newFiles);
    }
    setUploadedFiles(newFiles);
  };

  return (
    <ul className="flex w-full flex-col gap-2" aria-label="Uploaded files">
      {fileList.map((file, i) => {
        if (!(file instanceof File)) {
          return null; // Skip rendering if file is not a File instance
        }
        const src = URL.createObjectURL(file);
        const isPdf = file.type === 'application/pdf';
        return (
          <li
            key={file.name + i}
            data-testid={file.name + i}
            className="flex h-20 items-start gap-2 rounded p-2 shadow-lg"
          >
            <div className="h-16 w-16 overflow-hidden rounded">
              {isPdf ? (
                <embed
                  src={src}
                  type="application/pdf"
                  className="h-full w-full"
                />
              ) : (
                <img
                  src={src}
                  alt="Selected file thumbnail"
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <div className="w-full">
              <p className="text-body text-text-body w-[50vw] max-w-[560px] truncate overflow-ellipsis">
                {file.name}
              </p>
            </div>
            <button
              aria-label="Delete Image"
              className="cursor-pointer"
              onClick={() => handleDeleteFile(file)}
            >
              <X className="fill-text-body hover:stroke-text-body h-5 w-5 hover:stroke-[8px]" />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export const UploadPhoto: React.FC<UploadPhotoProps> = ({
  initialFiles,
  maxNumberOfUpload = 1,
  maxFileSize = MAX_FILE_SIZE,
  acceptedFileTypes = ACCEPTED_FILE_TYPES,
  errorPath,
  errorMessages,
  dropzoneLabel,
  ...props
}) => {
  const { isPdfAllowed } = usePdfConfig();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(
    initialFiles || [],
  );
  const [loading, setLoading] = useState(false);
  const id = uuidv4();

  useEffect(() => {
    if (initialFiles) {
      setUploadedFiles(initialFiles);
    }
  }, [initialFiles]);

  const handleUpload = () => {
    if (fileInputRef.current !== null) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    const imageFile = event.target.files?.[0];
    if (imageFile) {
      try {
        const isValidated = validateFile(
          imageFile,
          maxFileSize,
          acceptedFileTypes,
          isPdfAllowed,
        );
        const renameDuplicateFiles = detectDuplicateFilesAndRename(
          imageFile,
          uploadedFiles,
        );
        if (isValidated) {
          if (props.handleFileChange) {
            await props.handleFileChange(
              // use imageFile instead of resizedFile so that exif data can be preserved
              renameDuplicateFiles.length > 0 ? renameDuplicateFiles : [],
            );
          }
          if (props.handleUploadError) {
            props.handleUploadError('');
          }
          setUploadedFiles(
            // use resizeFile here to optimise performance and exif data is not needed to display the image
            renameDuplicateFiles.length > 0 ? renameDuplicateFiles : [],
          );
        }
        setLoading(false);
      } catch (error) {
        if (props.handleUploadError) {
          props.handleUploadError((error as Error).message);
        }
        setLoading(false);
      }
    } else {
      const uploadError = errorMessages?.find(
        (error) => error.path === errorPath,
      )?.message;
      if (props.handleUploadError && uploadError) {
        props.handleUploadError(uploadError);
      }
      setLoading(false);
    }
  };
  const computedAcceptedFileTypes = [
    ...acceptedFileTypes,
    ...(isPdfAllowed ? ['pdf'] : []),
  ];

  return (
    <section className="max-w-form mb-8 mt-6 flex w-full flex-col gap-4">
      <InlineErrorMessage path={errorPath} messages={errorMessages} />
      <Dropzone
        onClick={handleUpload}
        loading={loading}
        disabled={disableDropzone(maxNumberOfUpload, uploadedFiles)}
        label={dropzoneLabel}
      />
      <input
        aria-label="Upload Photo"
        id={props.id ?? id}
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={[
          'image/*',
          ...computedAcceptedFileTypes.map((type) => `.${type}`),
        ].join(', ')}
        onChange={handleFileChange}
        multiple={false}
        maxLength={maxNumberOfUpload}
        value=""
      />
      <ImageList
        fileList={uploadedFiles}
        handleDelete={props.handleDelete}
        setUploadedFiles={setUploadedFiles}
      />
    </section>
  );
};
